// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import CmsEditionMode from "@hardwoods/App/Components/CMS/CmsEditingMode";
import PrintService from "@hardwoods/App/Components/Common/PrintService";
import StyledOverlay from "@hardwoods/App/Components/Common/StyledOverlay";
import XngLoadingOverlay from "@hardwoods/App/Components/Common/XngLoadingOverlay";
import { Container } from "@hardwoods/App/Components/Container/Container";
import { CustomAppState } from "@hardwoods/App/Store/Reducers";
import { getCommon, getHeader } from "@hardwoods/App/Store/Selectors/CommonState";
import { constants } from "@hardwoods/XDL/constants";
import color from "@hardwoods/XDL/Utilities/color";
import EventBus from "@hardwoods/XDL/Utilities/EventBus";
import { xScreen } from "@hardwoods/XDL/Utilities/MediaQuery";
import { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import { HasShellContext, withIsInShell } from "@insite/client-framework/Components/IsInShell";
import Zone from "@insite/client-framework/Components/Zone";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import dismissRfqUpdates from "@insite/client-framework/Store/Context/Handlers/DismissRfqUpdates";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import PageModule from "@insite/client-framework/Types/PageModule";
import PageProps from "@insite/client-framework/Types/PageProps";
import SkipNav, { SkipNavStyles } from "@insite/content-library/Components/SkipNav";
import { GridItemProps } from "@insite/mobius/GridItem";
import Link from "@insite/mobius/Link";
import Page, { PageProps as ContainerProps } from "@insite/mobius/Page";
import { HasToasterContext, withToaster } from "@insite/mobius/Toast/ToasterContext";
import Typography from "@insite/mobius/Typography";
import { getProp } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: CustomAppState) => ({
    isSticky: getHeader(state).isSticky,
    stickyMenu: getHeader(state).stickyMenu,
    session: getSession(state),
    myAccountPageUrl: getPageLinkByPageType(state, "MyAccountPage")?.url,
    isMobile: getCommon(state).isMobile,
});

const mapDispatchToProps = {
    dismissRfqUpdates,
};

type Props = PageProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasToasterContext &
    HasHistory &
    HasShellContext;

export interface HeaderStyles {
    header?: ContainerProps;
    container?: ContainerProps;
    navigation?: InjectableCss;
    navigationPlaceholder?: InjectableCss;
    skipToContent?: SkipNavStyles;
    websiteName?: InjectableCss;
    printLogoContainer?: InjectableCss;
    secondaryMenuContainer?: InjectableCss;
    secondaryMenuGridItem?: GridItemProps;
}

export const headerStyles: HeaderStyles = {
    header: {
        as: "header",
        fullWidth: [true, true, true, true, true],
        padding: 0,
        css: css`
            position: relative;
            z-index: ${getProp("theme.zIndex.stickyFooter")};
            box-shadow: 0 2px 5px 0 ${color("#8c8c8c", 0.3)};
            ${xScreen.lte("sm")} {
                box-shadow: none;
            }
        `,
    },
    secondaryMenuContainer: {
        css: css`
            background-color: ${getColor("common.ui2")};
            height: 40px;
            > div {
                height: 100%;
                display: flex;
                align-items: center;
            }
            ${({ isCmsEditing }: any) =>
                isCmsEditing &&
                css`
                    height: 80px;
                    div[class^="WidgetStyle"] {
                        padding: 0 4px 0;
                    }
                `}

            ${xScreen.lte("sm")} {
                display: none;
            }
        `,
    },
    secondaryMenuGridItem: {
        width: [12, 6, 6, 6, 6],
        css: css`
            align-items: center;
        `,
    },
    navigation: {
        css: css`
            position: relative;
            transition: top 350ms ease-in;
            background-color: ${getColor("primary")};
            padding: 0;

            ${({ isSticky }: any) =>
                isSticky &&
                `   position: fixed;
                    top:-50px;
                    width: 100%;
            `}

            ${({ stickyMenu }: any) =>
                stickyMenu &&
                `top: ${constants.header.size.sticky}px;
            `}
            
            ${xScreen.lte("md")} {
                display: none;
            }
        `,
    },
    navigationPlaceholder: {
        css: css`
            ${({ isSticky }: any) => isSticky && `height:50px;`}
        `,
    },
    container: {
        as: "div",
        fullWidth: [true, true, false, false, false],
        padding: 0,
        background: "transparent",
        css: css`
            display: flex;
        `,
    },
    printLogoContainer: {
        css: css`
            @media print {
                display: block;
            }
            padding-left: 15px;
            display: none;
            ${({ isEditing }: any) => isEditing && "display: block;"}
        `,
    },
};

const styles = headerStyles;

const StyledWrapperX = getStyledWrapper("div") as any;

const Header = ({
    id,
    session,
    toaster,
    history,
    myAccountPageUrl,
    dismissRfqUpdates,
    isSticky,
    stickyMenu,
    isMobile,
    shellContext,
}: Props) => {
    const [showStyledOverlay, setShowStyledOverlay] = useState(false);
    const [forceShowStyledOverlay, setForceShowStyledOverlay] = useState(false);
    const viewMyDashboardClickHandler = () => {
        if (myAccountPageUrl) {
            history.push(myAccountPageUrl);
        }
    };

    function forceShowHeaderPopupOverlayHandler(e: any) {
        const data = e.detail;
        // By Default true
        if (data.forceShow !== undefined) {
            setForceShowStyledOverlay(data.forceShow);
        } else {
            setShowStyledOverlay(data.isShow ?? false);
        }
    }

    useEffect(() => {
        if (session.hasRfqUpdates && myAccountPageUrl) {
            toaster.addToast({
                body: (
                    <>
                        <Typography>{translate("You have new quote information.")}</Typography>
                        <Link onClick={viewMyDashboardClickHandler}>{translate("View My Dashboard")}</Link>
                    </>
                ),
                messageType: "info",
                timeoutLength: 1000 * 60 * 60 * 24 * 365,
                onClose: () => {
                    dismissRfqUpdates();
                },
            });
        }

        EventBus.on(constants.customEvents.forceShowHeaderPopupOverlay, forceShowHeaderPopupOverlayHandler);
        return () => {
            EventBus.remove(constants.customEvents.forceShowHeaderPopupOverlay, forceShowHeaderPopupOverlayHandler);
        };
    }, [myAccountPageUrl]);

    const afterHeader = React.createRef<HTMLSpanElement>();
    return (
        <>
            <StyledWrapperX isEditing={shellContext.isEditing} {...styles.printLogoContainer}>
                <CmsEditionMode editingChildren="Zone to display Website Logo in Print mode">
                    <Zone contentId={id} zoneName="PrintLogoZone" />
                </CmsEditionMode>
            </StyledWrapperX>
            <Page isHeader={true} {...styles.header}>
                <SkipNav
                    extendedStyles={styles.skipToContent}
                    text={translate("Skip to main content")}
                    destination={afterHeader}
                />
                <StyledWrapperX {...styles.secondaryMenuContainer} isCmsEditing={shellContext.isEditing}>
                    <Container>
                        <Zone contentId={id} zoneName="SecondaryMenu" />
                    </Container>
                </StyledWrapperX>
                <Zone contentId={id} zoneName="MainHeader" />
                <StyledWrapperX {...styles.navigationPlaceholder} isSticky={isSticky} stickyMenu={stickyMenu} />
                <StyledWrapperX {...styles.navigation} isSticky={isSticky} stickyMenu={stickyMenu}>
                    <Page {...styles.container}>
                        <Zone contentId={id} zoneName="MainNavigation" />
                    </Page>
                </StyledWrapperX>
                <CmsEditionMode editingChildren="Zone to display Header Notification">
                    <Zone contentId={id} zoneName="HeaderNotification" />
                </CmsEditionMode>
                <Zone contentId={id} zoneName="Breadcrumb" />
                <span ref={afterHeader} tabIndex={-1} />
            </Page>
            <StyledOverlay
                forceShow={forceShowStyledOverlay}
                show={showStyledOverlay || !isMobile}
                includeHeader={false}
                data-test-selector="overlay-exclude-header"
            />
            <XngLoadingOverlay />
            <PrintService />
        </>
    );
};

const pageModule: PageModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withIsInShell(withToaster(withHistory(Header)))),
    definition: {
        hasEditableTitle: false,
        hasEditableUrlSegment: false,
        pageType: "System",
    },
};

export const HeaderContext = "HeaderContext";

export default pageModule;
